'use client';

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = ({ className, ...props }: DialogPrimitive.Trigger.Props) => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" className={className} {...props} />;
};

const DialogPortal = DialogPrimitive.Portal;

const DialogBackdrop = ({ className, ...props }: DialogPrimitive.Backdrop.Props) => {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-backdrop"
      className={cn(
        'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      {...props}
    />
  );
};

// gloss
const DialogViewport = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <DialogPrimitive.Viewport
      data-slot="dialog-viewport"
      className={cn(
        'pointer-events-auto fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        className,
      )}
      {...props}
    />
  );
};

// gloss
const dialogPopupVariants = cva(
  'bg-background text-foreground border-border relative grid gap-4 border p-6 shadow-lg rounded-xl transition-[scale,opacity] duration-150 ease-out data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0',
  {
    variants: {
      size: {
        sm: 'w-full max-w-sm',
        default: 'w-full max-w-md',
        lg: 'w-full max-w-lg',
        xl: 'w-full max-w-xl',
        full: 'w-full max-w-2xl',
        wide: 'w-full max-w-5xl',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

type DialogPopupProps = DialogPrimitive.Popup.Props & VariantProps<typeof dialogPopupVariants>;

const DialogPopup = ({ className, size = 'default', ...props }: DialogPopupProps) => {
  return (
    <DialogPrimitive.Popup
      data-slot="dialog-popup"
      className={cn(dialogPopupVariants({ size, className }))}
      {...props}
    />
  );
};

const DialogHeader = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
};

const DialogFooter = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

const DialogTitle = ({ className, ...props }: DialogPrimitive.Title.Props) => {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-semibold text-lg leading-none tracking-tight', className)}
      {...props}
    />
  );
};

const DialogDescription = ({ className, ...props }: DialogPrimitive.Description.Props) => {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

const DialogClose = (props: DialogPrimitive.Close.Props) => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

type DialogContentProps = DialogPrimitive.Popup.Props &
  VariantProps<typeof dialogPopupVariants> & {
    hideClose?: boolean;
  };

const DialogContent = ({ className, size = 'default', hideClose = false, children, ...props }: DialogContentProps) => {
  return (
    <DialogPortal>
      <DialogBackdrop />
      <DialogViewport>
        <DialogPopup className={className} size={size} {...props}>
          {children}
          {!hideClose && (
            <DialogPrimitive.Close
              data-slot="dialog-close-button"
              className="absolute top-4 right-4 rounded-md text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
            >
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPopup>
      </DialogViewport>
    </DialogPortal>
  );
};

export type { DialogContentProps };
export {
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DialogViewport,
  dialogPopupVariants,
};
